import React, { useState, useEffect, FC } from 'react';
import {
    TextField, Grid, Box,
    List, ListItemButton, ListItemIcon, ListItemText,
    Checkbox, Button, Paper, Typography,
    InputAdornment,
    FormHelperText
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { nameValidation, normalizeString } from 'utils/Validations';
import { TimekeeperService } from 'services/TimekeeperService';
import { BalanceDomino } from 'repository/timekeeper/@types/BalanceDomino';
import { GroupBalance } from 'repository/timekeeper/@types/GroupBalance';
import { GroupBalanceSchema } from 'schemas/GroupBalance';

interface GroupBalanceFormData {
    name: string;
    description?: string | null;
    balances?: number[];
}

interface GroupBalanceFormProps {
    initialData: GroupBalanceFormData;
    onChange: (data: GroupBalanceFormData) => void;
    rows: GroupBalance[];
    excludeId?: number; // Para excluir el registro actual en validación de nombre (en edición)
}

const timekeeperService = new TimekeeperService();

function not(a: number[], b: number[]) {
    return a.filter((value) => b.indexOf(value) === -1);
}

function intersection(a: number[], b: number[]) {
    return a.filter((value) => b.indexOf(value) !== -1);
}

export const GroupBalanceForm: FC<GroupBalanceFormProps> = ({ initialData, onChange, rows, excludeId }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [data, setData] = useState<GroupBalanceFormData>(initialData);
    const [availableBalances, setAvailableBalances] = useState<BalanceDomino[]>([]);
    const [checked, setChecked] = useState<number[]>([]);
    const [errors, setErrors] = useState<{ name?: string; description?: string }>({});

    const [left, setLeft] = useState<number[]>([]);
    const [right, setRight] = useState<number[]>(initialData.balances || []);

    const leftChecked = intersection(checked, left);
    const rightChecked = intersection(checked, right);

    useEffect(() => {
        const loadBalances = async () => {
            try {
                const balances = await timekeeperService.balance_domino.getAll();
                setAvailableBalances(balances);

                const allIds = balances.map(b => b.balance_domino_id);
                const availableIds = not(allIds, initialData.balances || []);
                setLeft(availableIds);
            } catch (error) {
                console.error('Error loading balances:', error);
                enqueueSnackbar(t('accruals.balance_domino.dialog.alerts.load_balances_error'), { variant: 'error' });
            }
        };

        loadBalances();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        onChange(data);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const existName = (name: string) => {
        const normalizedInput = normalizeString(name);
        const existsInRows = rows.some((row: any) =>
            normalizeString(row.name) === normalizedInput &&
            (!excludeId || row.group_balance_id !== excludeId)
        );
        return existsInRows;
    };

    const validateField = (field: 'name' | 'description', value: any) => {
        const result = GroupBalanceSchema.shape[field].safeParse(value);
        if (!result.success) {
            const errorMessage = result.error.issues?.[0]?.message || 'Invalid value';
            setErrors(prev => ({ ...prev, [field]: errorMessage }));
            return false;
        } else {
            setErrors(prev => ({ ...prev, [field]: undefined }));
            return true;
        }
    };

    const handleToggle = (value: number) => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setChecked(newChecked);
    };

    const handleAllRight = () => {
        const newRight = right.concat(left);
        setRight(newRight);
        setLeft([]);
        setData({ ...data, balances: newRight });
    };

    const handleCheckedRight = () => {
        const newRight = right.concat(leftChecked);
        setRight(newRight);
        setLeft(not(left, leftChecked));
        setChecked(not(checked, leftChecked));
        setData({ ...data, balances: newRight });
    };

    const handleCheckedLeft = () => {
        setLeft(left.concat(rightChecked));
        const newRight = not(right, rightChecked);
        setRight(newRight);
        setChecked(not(checked, rightChecked));
        setData({ ...data, balances: newRight });
    };

    const handleAllLeft = () => {
        setLeft(left.concat(right));
        setRight([]);
        setData({ ...data, balances: [] });
    };

    const customList = (title: string, items: number[]) => (
        <Paper sx={{ width: '100%', height: 300, overflow: 'auto' }}>
            <Typography sx={{ p: 2, fontWeight: 'bold' }}>{title}</Typography>
            <List dense component="div" role="list">
                {items.map((value) => {
                    const labelId = `transfer-list-item-${value}-label`;
                    const balance = availableBalances.find(b => b.balance_domino_id === value);
                    if (!balance) return null;

                    return (
                        <ListItemButton
                            key={value}
                            role="listitem"
                            onClick={handleToggle(value)}
                        >
                            <ListItemIcon>
                                <Checkbox
                                    checked={checked.indexOf(value) !== -1}
                                    tabIndex={-1}
                                    disableRipple
                                    inputProps={{
                                        'aria-labelledby': labelId,
                                    }}
                                />
                            </ListItemIcon>
                            <ListItemText id={labelId} primary={balance.name} />
                        </ListItemButton>
                    );
                })}
            </List>
        </Paper>
    );

    return (
        <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <TextField
                    fullWidth
                    name="Name"
                    label={t("accruals.balance_domino.dialog.inputs.name")}
                    value={data.name}
                    autoComplete='off'
                    onChange={(e) => {
                        if (nameValidation(e.target.value)) {
                            const newData = { ...data, name: e.target.value };
                            setData(newData);
                            validateField('name', e.target.value);
                        }
                    }}
                    error={!!errors.name || (existName(data.name) && data.name !== '')}
                    slotProps={{
                        htmlInput: { maxLength: 100 },
                        input: {
                            endAdornment: <InputAdornment position="end">{`${data.name.length}/100`}</InputAdornment>,
                        },
                    }}
                />
                <FormHelperText sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, px: 1.75 }} component={'span'}>
                    <Box component={'span'} color={(theme) => theme.palette.error.main}>{errors.name || (existName(data.name) && data.name !== '' ? t('accruals.balance_domino.dialog.alerts.name_exist') : '')}</Box>
                </FormHelperText>
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <TextField
                    fullWidth
                    name="Description"
                    label={t("accruals.balance_domino.dialog.inputs.description")}
                    value={data.description || ''}
                    autoComplete='off'
                    onChange={(e) => {
                        const newData = { ...data, description: e.target.value || null };
                        setData(newData);
                        validateField('description', e.target.value || undefined);
                    }}
                    error={!!errors.description}
                    slotProps={{
                        htmlInput: { maxLength: 255 },
                        input: {
                            endAdornment: <InputAdornment position="end">{`${data.description?.length || 0}/255`}</InputAdornment>,
                        }
                    }}
                    rows={2}
                    multiline
                />
                <FormHelperText sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, px: 1.75 }} component={'span'}>
                    <Box component={'span'} color={(theme) => theme.palette.error.main}>{errors.description || ''}</Box>
                </FormHelperText>
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t("accruals.balance_domino.dialog.inputs.balances")}
                </Typography>
                <Grid container spacing={0} justifyContent="center" alignItems="center">
                    <Grid size={{ xs: 12, md: 5 }}>
                        {customList(t('accruals.balance_domino.dialog.inputs.available_balances'), left)}
                    </Grid>
                    <Grid size={{ xs: 2, md: 2 }}>
                        <Grid container direction="column" alignItems="center" spacing={1}>
                            <Grid>
                                <Button
                                    sx={{ my: 0.5 }}
                                    variant="outlined"
                                    size="small"
                                    onClick={handleAllRight}
                                    disabled={left.length === 0}
                                    aria-label="move all right"
                                >
                                    ≫
                                </Button>
                            </Grid>
                            <Grid>
                                <Button
                                    sx={{ my: 0.5 }}
                                    variant="outlined"
                                    size="small"
                                    onClick={handleCheckedRight}
                                    disabled={leftChecked.length === 0}
                                    aria-label="move selected right"
                                >
                                    &gt;
                                </Button>
                            </Grid>
                            <Grid>
                                <Button
                                    sx={{ my: 0.5 }}
                                    variant="outlined"
                                    size="small"
                                    onClick={handleCheckedLeft}
                                    disabled={rightChecked.length === 0}
                                    aria-label="move selected left"
                                >
                                    &lt;
                                </Button>
                            </Grid>
                            <Grid>
                                <Button
                                    sx={{ my: 0.5 }}
                                    variant="outlined"
                                    size="small"
                                    onClick={handleAllLeft}
                                    disabled={right.length === 0}
                                    aria-label="move all left"
                                >
                                    ≪
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid size={{ xs: 12, md: 5 }}>
                        {customList(t('accruals.balance_domino.dialog.inputs.selected_balances'), right)}
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};
